/**
 * EndGameResults - displays final scores and winner
 */

import { motion } from 'framer-motion';
import { socketActions } from '../../lib/socketActions';
import { useGameStore } from '../../stores/gameStore';
import { calculateScore } from '../../utils/game';

export function EndGameResults() {
  const gameState = useGameStore((s) => s.gameState);
  const playerId = useGameStore((s) => s.playerId);
  // Captured when GAME_ENDED arrived - later events can't change the winner
  const finalResults = useGameStore((s) => s.finalResults);

  // Check if current player is the host
  const isHost = gameState?.players.find(p => p.id === playerId)?.isHost ?? false;

  if (!gameState || gameState.status !== 'ended') return null;

  // Use stored final results, or calculate from state (e.g. rejoined after the end)
  const finalScores =
    finalResults
      ? finalResults.finalScores
      : gameState.players
          .map((player) => {
            const words = gameState.playerWords[player.id] || [];
            return {
              playerId: player.id,
              name: player.name,
              score: gameState.scores[player.id] || 0,
              words,
              longestWordLength: Math.max(0, ...words.map((w) => w.word.length)),
              rank: 0,
            };
          })
          .sort((a, b) => b.score - a.score)
          .map((p, i, arr) => ({
            ...p,
            rank:
              i > 0 && p.score === arr[i - 1].score ? arr[i - 1].rank : i + 1,
          }));

  const winner = finalResults ? finalResults.winner : finalScores[0] ?? null;
  const isWinner = winner?.playerId === playerId;

  return (
    <div className="min-h-content flex flex-col items-center justify-center p-4 safe-area-x">
      <motion.div
        className="w-full max-w-2xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Winner announcement */}
        <div className="text-center mb-8">
          <motion.div
            className="text-4xl mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
          >
            🏆
          </motion.div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {!winner ? 'Game Over!' : isWinner ? 'You Won!' : `${winner.name} Wins!`}
          </h2>
          {winner && (
            <p className="text-slate-600 dark:text-slate-400">
              with <span className="font-bold text-blue-600">{winner.score}</span>{' '}
              points
            </p>
          )}
        </div>

        {/* Final standings */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden mb-8">
          <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-700 dark:text-slate-200">Final Standings</h3>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {finalScores.map((player, index) => (
              <motion.div
                key={player.playerId}
                className={`p-4 ${
                  player.playerId === playerId ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-lg font-bold ${
                        player.rank === 1
                          ? 'text-yellow-500'
                          : player.rank === 2
                          ? 'text-slate-400'
                          : player.rank === 3
                          ? 'text-amber-600'
                          : 'text-slate-500'
                      }`}
                    >
                      #{player.rank}
                    </span>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {player.name}
                      {player.playerId === playerId && (
                        <span className="text-slate-400 dark:text-slate-500 ml-1">(you)</span>
                      )}
                    </span>
                  </div>
                  <span className="text-xl font-bold text-blue-600">
                    {player.score} pts
                  </span>
                </div>

                {/* Player's words */}
                {player.words.length > 0 && (
                  <div className="flex flex-wrap gap-1 sm:gap-2 mt-2">
                    {player.words.map((word) => (
                      <div
                        key={word.id}
                        className="flex items-center gap-0.5 bg-slate-100 dark:bg-slate-700 rounded px-1.5 sm:px-2 py-0.5 sm:py-1"
                      >
                        <span className="text-xs sm:text-sm font-mono">{word.word}</span>
                        <span className="text-xs text-slate-400 ml-1">
                          +{calculateScore(word.word.length)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 pb-[env(safe-area-inset-bottom,0px)]">
          {isHost ? (
            <button
              onClick={() => socketActions.rematch()}
              className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors"
            >
              Rematch
            </button>
          ) : (
            <div className="px-6 py-3 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-medium rounded-lg">
              Waiting for host to start rematch...
            </div>
          )}
          <button
            onClick={() => socketActions.leaveRoom()}
            className="px-6 py-3 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
          >
            Leave Game
          </button>
        </div>
      </motion.div>
    </div>
  );
}
